import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SendIcon from "@mui/icons-material/Send";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const emptyForm = {
  modelo: "notificacao_a",
  modo: "recriado",
  formatoSaida: "tira",
  ordemImposicao: "corte_empilhamento",
  emNome: "profissional", // em nome de quem o talão é impresso (checkbox da VISA)
  emitenteNome: "",
  emitenteConselho: "",
  emitenteEspecialidade: "",
  emitenteEndereco: "",
  emitenteTelefone: "",
  emitenteEmail: "",
  emitenteCpf: "",
  ocultarEndereco: false,
  ocultarTelefone: false,
  ocultarEmail: false,
  corTema: "",
  // Modelo comum: default é um molde real do catálogo (não o motor "Simples
  // (gerado)" antigo, que ficou amador e foi rejeitado pelo usuário — ver
  // memória do projeto). Sem isso, um receituário novo caía no motor velho
  // até o usuário clicar manualmente numa das miniaturas da galeria.
  estiloVisual: "molde:teal-caduceu",
  // Começa no genérico "VISA" — quem emite completa a cidade/órgão ("VISA-BH",
  // "VISA Sabará/MG", "SES/MG"). Cravar "-BH" aqui fazia talão de outra cidade
  // sair afirmando a vigilância errada.
  orgaoAutorizacao: "VISA",
  numeroAutorizacao: "",
  numeroInicialTexto: "",
  numeroFinalTexto: "",
  overlay: {},
  // Modelo "comum" (não controlado): sem faixa/autorização, com logo + pauta.
  logoFileName: "",
  corpoPautado: true,
  quantidade: 1,
};

// Numeração da autorização da VISA: 14 dígitos na máscara 0000.0-00.0000000
// (ex.: 2606.1-31.0076916). O 2º bloco identifica o modelo: .1 = A, .2 = B.
const DIGITOS = 14;
const BLOCO = 1e7; // último bloco tem 7 dígitos
const soDigitos = (s) => (s || "").replace(/\D/g, "");
const aplicarMascara = (s) => {
  const d = soDigitos(s).slice(0, DIGITOS);
  if (d.length <= 4) return d;
  if (d.length <= 5) return `${d.slice(0, 4)}.${d.slice(4)}`;
  if (d.length <= 7) return `${d.slice(0, 4)}.${d.slice(4, 5)}-${d.slice(5)}`;
  return `${d.slice(0, 4)}.${d.slice(4, 5)}-${d.slice(5, 7)}.${d.slice(7)}`;
};
// Quantidade de folhas derivada da faixa (final - inicial + 1).
const calcularFaixa = (ini, fim) => {
  const a = soDigitos(ini), b = soDigitos(fim);
  if (a.length !== DIGITOS || b.length !== DIGITOS) return null;
  const qtd = Number(b) - Number(a) + 1;
  if (qtd <= 0) return { erro: "A numeração final é menor que a inicial." };
  if (qtd > 5000) return { erro: "Faixa muito grande (acima de 5000 folhas)." };
  // Guarda-corpo: com bloco de 7 dígitos, só viraria a cada 10 milhões de folhas.
  const cruzaVirada = Math.floor(Number(b) / BLOCO) !== Math.floor(Number(a) / BLOCO);
  return { quantidade: qtd, cruzaVirada };
};

const formatCnpj = (s) => {
  const d = (s || "").replace(/\D/g, "").slice(0, 14);
  return d.length === 14
    ? d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
    : (s || "");
};

// Monta os campos do emitente conforme a caixa marcada na autorização:
//  - profissional: nome/CRM/especialidade/endereço comercial do médico (caixa 1)
//  - estabelecimento: razão social + CNPJ + endereço da instituição (caixa 2)
// Sempre editável depois — isto só define o preenchimento base.
const camposEmitente = (d, origem) => {
  if (origem === "estabelecimento") return {
    emitenteNome: d.instituicaoNome || "",
    emitenteConselho: d.instituicaoCnpj ? `CNPJ ${formatCnpj(d.instituicaoCnpj)}` : "",
    emitenteEspecialidade: "",
    emitenteTelefone: d.telefone || "",
    emitenteEndereco: d.instituicaoEndereco || d.endereco || "",
  };
  return {
    emitenteNome: d.profissionalNome || "",
    emitenteConselho: d.conselho || "",
    emitenteEspecialidade: d.especialidade || "",
    emitenteTelefone: d.telefone || "",
    emitenteEndereco: d.endereco || "",
  };
};

// Amostra visual da cor do PAPEL de cada modelo (a cor real vem do papel na
// impressora — o PDF é fundo branco). Chave = campo `papel` de /receituarios/models.
const PAPEL_SWATCH = {
  amarelo: { bg: "#f5df4d", nome: "AMARELO" },
  azul: { bg: "#8ecae6", nome: "AZUL" },
  branco: { bg: "#ffffff", nome: "BRANCO" },
};

// Dados de amostra pra renderizar a galeria de modelos — não depende do que o
// usuário já digitou no formulário, sempre mostra o mesmo exemplo neutro.
const MOLDE_THUMB_SAMPLE = {
  modelo: "comum",
  quantidade: 1,
  emitenteNome: "Dra. Ana Souza",
  emitenteConselho: "CRM-MG 00000",
  emitenteEspecialidade: "Clínica Geral",
  emitenteTelefone: "(31) 90000-0000",
  emitenteEmail: "ana.souza@email.com",
  emitenteEndereco: "Rua Exemplo, 100 - BH/MG",
};
// Cache em módulo: thumbnails não mudam entre uma abertura e outra do diálogo
// na mesma sessão — evita gerar de novo (cada uma custa um PDF+rasterização).
const moldeThumbCache = {};

// `initialTicketId`/`initialContact` chegam quando o diálogo é aberto pela gaveta
// do ticket (mesmo padrão da OS e do Orçamento). Com o ticket conhecido, a prova
// pode ser enviada ao cliente direto daqui.
const ReceituarioDialog = ({ open, onClose, receituarioId, onSaved, initialTicketId, initialContact }) => {
  const [form, setForm] = useState(emptyForm);
  const [models, setModels] = useState([]);
  // Catálogo de moldes visuais do receituário COMUM (PDFs reais). Vazio = só
  // o layout gerado padrão. Ver receitaComumMolde.ts no backend.
  const [moldesComum, setMoldesComum] = useState([]);
  // Thumbnails da galeria de modelos — object URLs cacheadas em módulo (persistem
  // entre aberturas do diálogo na mesma sessão, já que os moldes não mudam).
  const [moldeThumbs, setMoldeThumbs] = useState({});
  const [moldeThumbsLoading, setMoldeThumbsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savedId, setSavedId] = useState(receituarioId || null);
  const [pdfUrl, setPdfUrl] = useState(null);
  // Preview ao vivo (PNG página 1) — atualiza conforme edita, SEM gerar/consumir
  // faixa. É o "ver sem gerar" (pedido do usuário 24/07).
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [ultimoNumero, setUltimoNumero] = useState(null);
  // Faixas já existentes que cruzam a informada (numeração do SNCR é única).
  const [conflitos, setConflitos] = useState([]);
  const [rascunhosSobrepostos, setRascunhosSobrepostos] = useState([]);
  // Aprovação da arte pelo cliente — pré-requisito para imprimir.
  const [aprovacaoOpen, setAprovacaoOpen] = useState(false);
  const [aprovacaoObs, setAprovacaoObs] = useState("");
  const [aprovando, setAprovando] = useState(false);
  // Importação da autorização da VISA (PDF) → pré-preenchimento.
  const [importando, setImportando] = useState(false);
  const [enviandoProva, setEnviandoProva] = useState(false);
  const [faltando, setFaltando] = useState([]);
  // Autorização com MAIS DE UMA receita (ex.: B + B2 + retinoides): o modal
  // deixa escolher quais gerar; cada uma vira um receituário separado.
  const [importPendente, setImportPendente] = useState(null);
  const [criandoReceitas, setCriandoReceitas] = useState(false);
  // Confirmação antes de imprimir: cada modelo usa papel de outra cor — o aviso
  // dá tempo de trocar o papel na impressora.
  const [papelConfirmOpen, setPapelConfirmOpen] = useState(false);

  const selectedModel = Array.isArray(models) ? models.find((m) => m.key === form.modelo) : undefined;
  // Modelo "comum" (não controlado) não tem faixa SNCR nem autorização VISA —
  // esses campos nem aparecem na tela para ele (ver JSX abaixo). A quantidade
  // vem de um campo simples, não de faixa.
  const ehComum = form.modelo === "comum";
  const numeroPos = form.overlay?.numero || selectedModel?.defaultOverlay?.numero || { x: 45, y: 139.5, size: 11 };
  const faixa = ehComum ? null : calcularFaixa(form.numeroInicialTexto, form.numeroFinalTexto);
  const podeGerar = ehComum
    ? Number(form.quantidade) > 0
    : !!faixa && !faixa.erro && conflitos.length === 0;

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const setChk = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.checked }));

  // Preview ao vivo: quando o formulário muda, pede o PNG da página 1 (debounce
  // 500ms). Não consome faixa nem persiste — é render puro. Só roda com modelo
  // escolhido e nome do emitente preenchido (senão a prévia fica vazia à toa).
  useEffect(() => {
    if (!open || !form.modelo || !form.emitenteNome) return;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const { data } = await api.post("/receituarios/preview", form, { responseType: "blob" });
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(data);
        });
      } catch { /* preview é best-effort — silencioso */ }
      finally { setPreviewLoading(false); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, JSON.stringify(form)]);

  // Libera o object URL do preview ao fechar.
  useEffect(() => {
    if (!open) setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
  }, [open]);

  // Galeria de modelos: busca 1 thumbnail por molde (dado de amostra fixo, não
  // o que o usuário digitou) — cacheado em módulo, só busca o que falta.
  useEffect(() => {
    if (!open || !moldesComum.length) return;
    const faltando = moldesComum.filter((m) => !moldeThumbCache[m.slug]);
    if (!faltando.length) {
      setMoldeThumbs({ ...moldeThumbCache });
      return;
    }
    let cancelado = false;
    setMoldeThumbsLoading(true);
    (async () => {
      for (const m of faltando) {
        if (cancelado) break;
        try {
          const { data } = await api.post(
            "/receituarios/preview",
            { ...MOLDE_THUMB_SAMPLE, estiloVisual: `molde:${m.slug}` },
            { responseType: "blob" }
          );
          moldeThumbCache[m.slug] = URL.createObjectURL(data);
          if (!cancelado) setMoldeThumbs({ ...moldeThumbCache });
        } catch { /* thumbnail é best-effort — sem imagem, mostra só o nome */ }
      }
      if (!cancelado) setMoldeThumbsLoading(false);
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, moldesComum]);

  // Troca "em nome de": re-preenche os campos do emitente a partir dos dados
  // importados (se houver). Sem import, só muda a marca — o operador edita à mão.
  const trocarOrigem = (origem) => setForm((f) => ({
    ...f, emNome: origem,
    ...(f._import ? camposEmitente(f._import, origem) : {}),
  }));

  const loadModels = useCallback(async () => {
    try {
      const { data } = await api.get("/receituarios/models");
      setModels(Array.isArray(data) ? data : []);
    } catch (err) { toastError(err); }
    try {
      const { data } = await api.get("/receituarios/moldes-comum");
      setMoldesComum(Array.isArray(data) ? data : []);
    } catch { /* catálogo é opcional */ }
  }, []);

  const loadReceituario = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/receituarios/${id}`);
      setForm({
        ...emptyForm,
        ...data,
        orgaoAutorizacao: data.orgaoAutorizacao || "VISA",
        numeroAutorizacao: data.numeroAutorizacao || "",
        numeroInicialTexto: data.numeroInicialTexto || "",
        numeroFinalTexto: data.numeroFinalTexto || "",
        overlay: data.overlay || {},
      });
      setUltimoNumero(data.ultimoNumeroTexto);

      // O PDF já gerado continua no servidor. Sem carregá-lo aqui, imprimir e
      // baixar dependeriam de gerar tudo de novo — e a numeração é finita.
      if (data.arquivoFinalFileName) {
        try {
          const resp = await api.get(`/receituarios/${id}/pdf`, { responseType: "blob" });
          setPdfUrl((antigo) => {
            if (antigo) URL.revokeObjectURL(antigo);
            return URL.createObjectURL(resp.data);
          });
        } catch { /* sem PDF acessível: os botões seguem desabilitados */ }
      }
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadModels();
    setPdfUrl(null);
    setSavedId(receituarioId || null);
    if (receituarioId) loadReceituario(receituarioId);
    else setForm({
      ...emptyForm,
      ticketId: initialTicketId || null,
      contactId: initialContact?.id || null,
    });
  }, [open, receituarioId, loadModels, loadReceituario, initialTicketId, initialContact]);

  // Checa sobreposição com faixas já cadastradas assim que a faixa fica completa.
  useEffect(() => {
    if (!open || !faixa || faixa.erro) { setConflitos([]); setRascunhosSobrepostos([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/receituarios/faixa", {
          params: {
            inicial: form.numeroInicialTexto,
            final: form.numeroFinalTexto,
            ...(savedId ? { excluirId: savedId } : {}),
          },
        });
        setConflitos(data.conflitos || []);
        setRascunhosSobrepostos(data.rascunhosSobrepostos || []);
      } catch {
        setConflitos([]); setRascunhosSobrepostos([]);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.numeroInicialTexto, form.numeroFinalTexto, savedId]);

  const buildBody = () => ({
    modelo: form.modelo,
    // Modelo comum: só existe modo "recriado" (layout próprio) — nunca manda
    // "arte_propria" pra ele, mesmo que o form ainda tenha o valor de uma
    // troca de modelo anterior.
    modo: ehComum ? "recriado" : form.modo,
    formatoSaida: form.formatoSaida,
    ordemImposicao: form.ordemImposicao,
    emitenteNome: form.emitenteNome,
    emitenteConselho: form.emitenteConselho,
    emitenteEspecialidade: form.emitenteEspecialidade,
    emitenteEndereco: form.emitenteEndereco,
    emitenteTelefone: form.emitenteTelefone,
    emitenteEmail: form.emitenteEmail,
    emitenteCpf: form.emitenteCpf,
    ocultarEndereco: form.ocultarEndereco,
    ocultarTelefone: form.ocultarTelefone,
    ocultarEmail: form.ocultarEmail,
    corTema: form.corTema || null,
    ...(ehComum
      ? {
          // Modelo comum: SEM faixa SNCR, SEM autorização VISA — nunca manda
          // esses campos, mesmo que tenham sobrado de uma importação anterior
          // no mesmo formulário (troca de modelo não deveria "vazar" faixa).
          orgaoAutorizacao: null,
          numeroAutorizacao: null,
          numeroInicialTexto: null,
          numeroFinalTexto: null,
          logoFileName: form.logoFileName || null,
          corpoPautado: form.corpoPautado !== false,
          quantidade: Math.max(1, parseInt(form.quantidade, 10) || 1),
        }
      : {
          // Sem estes dois o número da autorização (que sai no rodapé) nunca persistia.
          numeroAutorizacao: form.numeroAutorizacao,
          orgaoAutorizacao: form.orgaoAutorizacao || null,
          ...(form.autorizacaoFileName ? { autorizacaoFileName: form.autorizacaoFileName } : {}),
          numeroInicialTexto: form.numeroInicialTexto,
          numeroFinalTexto: form.numeroFinalTexto,
          overlay: { ...form.overlay, numero: numeroPos },
        }),
    ...(form.ticketId ? { ticketId: form.ticketId } : {}),
    ...(form.contactId ? { contactId: form.contactId } : {}),
  });

  // Cria (se novo) ou atualiza, devolvendo o id.
  const ensureSaved = async () => {
    const body = buildBody();
    if (savedId) {
      await api.put(`/receituarios/${savedId}`, body);
      return savedId;
    }
    const { data } = await api.post("/receituarios", body);
    setSavedId(data.id);
    return data.id;
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    try {
      await ensureSaved();
      toast.success("Rascunho salvo.");
      onSaved && onSaved();
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  // Aplica os dados importados no formulário. `receita` escolhe modelo + faixa
  // quando a autorização traz mais de uma; sem ela vale o que o parser sugeriu.
  const aplicarImportacao = (data, receita) => {
    // "Em nome de": vem do checkbox da VISA; se o ✔ não estava na camada de
    // texto (emNome null), infere pela caixa 2 preenchida — o operador confere.
    const origem = data.emNome || (data.instituicaoNome && data.instituicaoCnpj ? "estabelecimento" : "profissional");
    setForm((f) => ({
      ...f,
      modelo: receita?.modelo || data.modelo || f.modelo,
      emNome: origem,
      _import: {
        profissionalNome: data.profissionalNome, conselho: data.conselho,
        especialidade: data.especialidade, telefone: data.telefone, endereco: data.endereco,
        instituicaoNome: data.instituicaoNome, instituicaoCnpj: data.instituicaoCnpj,
        instituicaoEndereco: data.instituicaoEndereco,
      },
      ...camposEmitente(data, origem),
      numeroAutorizacao: data.numeroAutorizacao || f.numeroAutorizacao,
      numeroInicialTexto: receita?.numeroInicialTexto || data.numeroInicialTexto || f.numeroInicialTexto,
      numeroFinalTexto: receita?.numeroFinalTexto || data.numeroFinalTexto || f.numeroFinalTexto,
      autorizacaoFileName: data.autorizacaoFileName,
    }));
    setFaltando(data.faltando || []);
    if (!data.emNome) {
      toast.info("Confirme: o talão é em nome do PROFISSIONAL ou do ESTABELECIMENTO?");
    }
    if (data.faltando?.length) {
      toast.warn(`Importado, mas confira: ${data.faltando.join(", ")}.`);
    } else {
      toast.success("Autorização importada. Confira os dados antes de gerar.");
    }
  };

  // Lê a autorização da VISA e pré-preenche a tela. É sugestão: tudo editável.
  const handleImportarAutorizacao = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportando(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/receituarios/importar-autorizacao", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if ((data.receitas || []).length > 1) {
        // Várias receitas na mesma autorização: escolher quais gerar. Antes,
        // preencher direto fazia a segunda sobrescrever a primeira.
        setImportPendente({ dados: data, marcadas: data.receitas.map(() => true) });
      } else {
        aplicarImportacao(data, data.receitas?.[0]);
      }
    } catch (err) { toastError(err); }
    finally { setImportando(false); }
  };

  // Confirma a escolha: a 1ª receita marcada fica neste formulário; as demais
  // viram receituários próprios (rascunhos) — cada talão segue seu fluxo de
  // prova, aprovação e impressão separado.
  const handleConfirmarReceitas = async () => {
    const { dados, marcadas } = importPendente;
    const escolhidas = dados.receitas.filter((_, i) => marcadas[i]);
    if (escolhidas.length === 0) {
      toast.warn("Marque ao menos uma receita.");
      return;
    }
    setCriandoReceitas(true);
    try {
      const [primeira, ...resto] = escolhidas;
      aplicarImportacao(dados, primeira);

      // MESMA origem (profissional × estabelecimento) para TODOS os modelos da
      // autorização — senão talões da mesma autorização sairiam com emitentes
      // diferentes (inconsistente e ilegal). Portaria 344/98: identificação única.
      const origem = dados.emNome || (dados.instituicaoNome && dados.instituicaoCnpj ? "estabelecimento" : "profissional");
      const emit = camposEmitente(dados, origem);

      const criadas = [];
      for (const r of resto) {
        const body = {
          modelo: r.modelo,
          modo: form.modo,
          formatoSaida: form.formatoSaida,
          ordemImposicao: form.ordemImposicao,
          ...emit,
          numeroAutorizacao: dados.numeroAutorizacao || "",
          orgaoAutorizacao: form.orgaoAutorizacao || null,
          ...(dados.autorizacaoFileName ? { autorizacaoFileName: dados.autorizacaoFileName } : {}),
          numeroInicialTexto: r.numeroInicialTexto,
          numeroFinalTexto: r.numeroFinalTexto,
          ...(form.ticketId || initialTicketId ? { ticketId: form.ticketId || initialTicketId } : {}),
          ...(form.contactId || initialContact?.id ? { contactId: form.contactId || initialContact?.id } : {}),
        };
        const { data: novo } = await api.post("/receituarios", body);
        const m = Array.isArray(models) ? models.find((x) => x.key === r.modelo) : null;
        criadas.push(`${novo.numero} (${m?.label || r.modelo})`);
      }
      if (criadas.length > 0) {
        toast.success(
          `Criado(s) também: ${criadas.join(", ")}. Abra cada um na lista para gerar e enviar a prova.`,
          { autoClose: 12000 }
        );
        onSaved && onSaved();
      }
      setImportPendente(null);
    } catch (err) { toastError(err); }
    finally { setCriandoReceitas(false); }
  };

  const handleUploadArte = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const id = await ensureSaved();
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/receituarios/${id}/arte`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, modo: "arte_propria" }));
      toast.success("Arte enviada.");
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  // Logo do emitente — exclusiva do modelo "comum". Ao contrário da arte
  // própria, NÃO muda `modo`: a logo é só um elemento do cabeçalho do layout
  // recriado do modelo comum.
  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setLoading(true);
    try {
      const id = await ensureSaved();
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/receituarios/${id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logoFileName: data.logoFileName }));
      toast.success("Logo enviada.");
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  const adjustNumero = (axis, delta) => {
    setForm((f) => {
      const cur = f.overlay?.numero || numeroPos;
      return { ...f, overlay: { ...f.overlay, numero: { ...cur, [axis]: (Number(cur[axis]) || 0) + delta } } };
    });
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const id = await ensureSaved();
      const { data } = await api.post(`/receituarios/${id}/generate`, buildBody());
      setUltimoNumero(data.ultimoNumeroTexto);
      if (data.cruzaVirada) {
        toast.warn("A faixa cruza a virada do último bloco. Confira a numeração na autorização.");
      }
      // Busca o PDF autenticado como blob (rota isAuth não recebe Bearer em nova aba)
      const resp = await api.get(`/receituarios/${id}/pdf`, { responseType: "blob" });
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(resp.data));
      toast.success(`Gerado: ${data.quantidade} folha(s).`);
      onSaved && onSaved();
    } catch (err) { toastError(err); }
    finally { setGenerating(false); }
  };

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `${form.numero || "receituario"}.pdf`;
    a.click();
  };

  const imprimir = async () => {
    if (!pdfUrl) return;
    const w = window.open(pdfUrl);
    if (w) w.addEventListener("load", () => w.print());
    // Registra o responsável pela impressão (é o usuário logado, nunca digitado).
    if (savedId) {
      try {
        const { data } = await api.post(`/receituarios/${savedId}/marcar-impresso`);
        setForm((f) => ({ ...f, impressoPor: data.impressoPor, impressoEm: data.impressoEm }));
        onSaved && onSaved();
      } catch { /* impressão já disparou; o registro é secundário */ }
    }
  };

  // Produzir o talão sem a arte aprovada pelo cliente é o erro que este passo
  // existe para evitar. Sem aprovação, o botão pede confirmação antes.
  // Aprovado, ainda passa pela confirmação do PAPEL: cada modelo imprime em
  // papel de outra cor, e a pausa dá tempo de trocar na impressora.
  const handlePrint = () => {
    if (form.arteAprovada) { setPapelConfirmOpen(true); return; }
    setAprovacaoOpen(true);
  };

  // Envia a PROVA (imagem com marca d'água) ao chat do cliente. Só aparece
  // quando o diálogo foi aberto a partir de um ticket.
  const handleEnviarProva = async () => {
    const tid = form.ticketId || initialTicketId;
    if (!savedId || !tid) return;
    setEnviandoProva(true);
    try {
      await api.post(`/receituarios/${savedId}/send/${tid}`);
      setForm((f) => ({ ...f, arteEnviadaEm: new Date().toISOString(), status: "enviado" }));
      toast.success("Prova enviada ao cliente. Aguarde a aprovação.");
      onSaved && onSaved();
    } catch (err) { toastError(err); }
    finally { setEnviandoProva(false); }
  };

  const handleConfirmarAprovacao = async () => {
    if (!savedId) return;
    setAprovando(true);
    try {
      const { data } = await api.post(`/receituarios/${savedId}/aprovar-arte`, {
        aprovada: true,
        observacao: aprovacaoObs || undefined,
      });
      setForm((f) => ({ ...f, ...data }));
      toast.success("Aprovação registrada.");
      setAprovacaoOpen(false);
      onSaved && onSaved();
      setPapelConfirmOpen(true);
    } catch (err) { toastError(err); }
    finally { setAprovando(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {receituarioId ? `Receituário ${form.numero || ""}` : "Novo receituário"}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <div style={{ textAlign: "center", padding: 32 }}><CircularProgress /></div>
        ) : (
          <Grid container spacing={2}>
            {/* Coluna esquerda: dados */}
            <Grid item xs={12} md={(pdfUrl || previewUrl) ? 6 : 12}>
              <Grid container spacing={2}>
                {/* Importar autorização da VISA só faz sentido no CONTROLADO —
                    o comum não tem autorização. Escondido no comum pra não ser a
                    primeira coisa (confusa) que o atendente vê num bloquinho simples. */}
                {!ehComum && (
                  <Grid item xs={12}>
                    <Button component="label" variant="outlined" size="small" fullWidth
                      disabled={importando}
                      startIcon={importando ? <CircularProgress size={16} /> : <UploadFileIcon />}>
                      Importar autorização da VISA (PDF)
                      <input type="file" accept="application/pdf" hidden onChange={handleImportarAutorizacao} />
                    </Button>
                    <Typography variant="caption" color="textSecondary">
                      Preenche prescritor, modelo e faixa a partir do PDF. Confira antes de gerar — tudo é editável.
                    </Typography>
                    {faltando.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Não consegui ler do PDF: <b>{faltando.join(", ")}</b>. Preencha à mão.
                      </Alert>
                    )}
                  </Grid>
                )}

                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Modelo</InputLabel>
                    <Select value={form.modelo} label="Modelo" onChange={set("modelo")}>
                      {models.map((m) => (
                        <MenuItem key={m.key} value={m.key} disabled={!m.hasLayout && form.modo === "recriado"}>
                          {m.label}{!m.hasLayout ? " (só arte própria)" : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {!ehComum && (
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Base</InputLabel>
                      <Select value={form.modo} label="Base" onChange={set("modo")}>
                        <MenuItem value="recriado">Modelo oficial</MenuItem>
                        <MenuItem value="arte_propria">Arte própria</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12} sm={ehComum ? 4 : 3}>
                  <FormControl fullWidth size="small" disabled={!ehComum && form.modo !== "recriado"}>
                    <InputLabel>Formato</InputLabel>
                    <Select value={form.formatoSaida} label="Formato" onChange={set("formatoSaida")}>
                      {ehComum ? (
                        [
                          <MenuItem key="meia_a4" value="meia_a4">Meia-A4 paisagem (210×148,5mm)</MenuItem>,
                          <MenuItem key="a5" value="a5">A5 retrato (148×210mm)</MenuItem>,
                        ]
                      ) : (
                        [
                          <MenuItem key="tira" value="tira">Tira (200×60mm)</MenuItem>,
                          <MenuItem key="a4" value="a4">A4 (4 por folha)</MenuItem>,
                        ]
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                {ehComum && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Quantidade de folhas" type="number" fullWidth size="small"
                      inputProps={{ min: 1 }}
                      value={form.quantidade || 1}
                      onChange={(e) => setForm((f) => ({ ...f, quantidade: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                      helperText="Este modelo não tem faixa SNCR — a tiragem é só a quantidade."
                    />
                  </Grid>
                )}

                {!ehComum && form.modo === "recriado" && form.formatoSaida === "a4" && (
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Esquema de corte</InputLabel>
                      <Select value={form.ordemImposicao} label="Esquema de corte" onChange={set("ordemImposicao")}>
                        <MenuItem value="corte_empilhamento">
                          Corte e empilhamento — fileiras correspondem entre as folhas
                        </MenuItem>
                        <MenuItem value="sequencial">
                          Sequencial — numeração corre na leitura da folha
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="textSecondary">
                      {form.ordemImposicao === "corte_empilhamento"
                        ? "Imprima a tiragem, empilhe e dê um corte por divisa: cada fileira já sai como um monte em sequência. Junte os montes na ordem das fileiras."
                        : "A numeração corre dentro de cada folha. Use se for cortar folha a folha."}
                    </Typography>
                  </Grid>
                )}

                {!ehComum && form.modo === "arte_propria" && (
                  <Grid item xs={12}>
                    <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />}>
                      {form.arteBaseFileName ? "Trocar arte (PDF)" : "Enviar arte (PDF)"}
                      <input type="file" accept="application/pdf" hidden onChange={handleUploadArte} />
                    </Button>
                    {form.arteBaseFileName && (
                      <Typography variant="caption" sx={{ ml: 1 }}>{form.arteBaseFileName}</Typography>
                    )}
                  </Grid>
                )}

                {ehComum && moldesComum.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.5 }}>
                      Modelo visual {moldeThumbsLoading && "— carregando prévias…"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      <ButtonBase
                        onClick={() => setForm((f) => ({ ...f, estiloVisual: "ondas" }))}
                        sx={{
                          width: 84, height: 106, borderRadius: 1,
                          border: "2px solid",
                          borderColor: !String(form.estiloVisual || "").startsWith("molde:") ? "primary.main" : "divider",
                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                          gap: 0.5, p: 0.5, textAlign: "center",
                        }}
                      >
                        <DescriptionOutlinedIcon color="disabled" />
                        <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.15 }}>Simples (gerado)</Typography>
                      </ButtonBase>
                      {moldesComum.map((m) => {
                        const value = `molde:${m.slug}`;
                        const selected = form.estiloVisual === value;
                        return (
                          <ButtonBase
                            key={m.slug}
                            onClick={() => setForm((f) => ({ ...f, estiloVisual: value }))}
                            sx={{
                              width: 84, height: 106, borderRadius: 1,
                              border: "2px solid",
                              borderColor: selected ? "primary.main" : "divider",
                              display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "flex-start",
                              overflow: "hidden", p: 0,
                            }}
                          >
                            {moldeThumbs[m.slug] ? (
                              <img
                                src={moldeThumbs[m.slug]}
                                alt={m.label}
                                style={{ width: "100%", height: 82, objectFit: "cover", objectPosition: "top" }}
                              />
                            ) : (
                              <Box sx={{ width: "100%", height: 82, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <CircularProgress size={16} />
                              </Box>
                            )}
                            <Typography variant="caption" sx={{ fontSize: 9, lineHeight: 1.15, px: 0.5, py: 0.25 }}>
                              {m.label.replace(" (A5)", "")}
                            </Typography>
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  </Grid>
                )}
                {ehComum && String(form.estiloVisual || "").startsWith("molde:") && (
                  <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2">Cor do modelo:</Typography>
                    <input
                      type="color"
                      value={form.corTema || moldesComum.find((m) => `molde:${m.slug}` === form.estiloVisual)?.cor || "#158A8A"}
                      onChange={(e) => setForm((f) => ({ ...f, corTema: e.target.value }))}
                      style={{ width: 36, height: 36, border: "none", cursor: "pointer", background: "none" }}
                    />
                    {form.corTema && (
                      <Button size="small" onClick={() => setForm((f) => ({ ...f, corTema: "" }))}>
                        Usar cor original
                      </Button>
                    )}
                  </Grid>
                )}
                {ehComum && (
                  <Grid item xs={12}>
                    <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />}>
                      {form.logoFileName ? "Trocar logo" : "Enviar logo (opcional)"}
                      <input type="file" accept="image/png,image/jpeg" hidden onChange={handleUploadLogo} />
                    </Button>
                    {form.logoFileName && (
                      <Typography variant="caption" sx={{ ml: 1 }}>{form.logoFileName}</Typography>
                    )}
                    <FormControlLabel
                      sx={{ ml: 2 }}
                      control={<Checkbox size="small" checked={form.corpoPautado !== false} onChange={setChk("corpoPautado")} />}
                      label="Corpo pautado (linhas horizontais)"
                    />
                  </Grid>
                )}

                <Grid item xs={12}><Divider textAlign="left"><Typography variant="caption">{ehComum ? "Dados do profissional" : "Identificação do emitente"}</Typography></Divider></Grid>
                {/* "Impresso em nome de" é conceito da autorização da VISA — não
                    existe no receituário comum, só confunde. Só no controlado. */}
                {!ehComum && (
                  <Grid item xs={12}>
                    <TextField select label="Impresso em nome de" size="small" value={form.emNome || "profissional"}
                      onChange={(e) => trocarOrigem(e.target.value)}
                      helperText="Vem do ✔ da autorização (caixa do topo). Troca aqui repreenche os dados do emitente."
                      sx={{ minWidth: 280 }}>
                      <MenuItem value="profissional">Do profissional de saúde</MenuItem>
                      <MenuItem value="estabelecimento">Do estabelecimento / instituição</MenuItem>
                    </TextField>
                  </Grid>
                )}
                <Grid item xs={12} sm={8}>
                  <TextField label="Nome do profissional / instituição" fullWidth size="small" value={form.emitenteNome || ""} onChange={set("emitenteNome")} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label="Conselho + nº + UF" placeholder="CRM-MG 12345" fullWidth size="small" value={form.emitenteConselho || ""} onChange={set("emitenteConselho")} />
                </Grid>
                <Grid item xs={12} sm={ehComum ? 12 : 6}>
                  <TextField label="Especialidade" fullWidth size="small" value={form.emitenteEspecialidade || ""} onChange={set("emitenteEspecialidade")} />
                </Grid>
                {/* CPF do prescritor é dado do talão controlado (SNCR) — não vai
                    num bloquinho simples. Só no controlado. */}
                {!ehComum && (
                  <Grid item xs={12} sm={6}>
                    <TextField label="CPF (opcional)" placeholder="000.000.000-00" fullWidth size="small" value={form.emitenteCpf || ""} onChange={set("emitenteCpf")} helperText="Só o CPF, sem outros dados" />
                  </Grid>
                )}
                <Grid item xs={12} sm={ehComum ? 6 : 12}>
                  <TextField label="Endereço completo" fullWidth size="small" value={form.emitenteEndereco || ""} onChange={set("emitenteEndereco")} disabled={form.ocultarEndereco} />
                </Grid>
                {ehComum && (
                  <Grid item xs={12} sm={6}>
                    <TextField label="Email" fullWidth size="small" value={form.emitenteEmail || ""} onChange={set("emitenteEmail")} />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField label="Telefone" fullWidth size="small" value={form.emitenteTelefone || ""} onChange={set("emitenteTelefone")} disabled={form.ocultarTelefone} />
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <FormControlLabel control={<Checkbox size="small" checked={!!form.ocultarEndereco} onChange={setChk("ocultarEndereco")} />} label="Ocultar endereço" />
                  <FormControlLabel control={<Checkbox size="small" checked={!!form.ocultarTelefone} onChange={setChk("ocultarTelefone")} />} label="Ocultar telefone" />
                  {ehComum && (
                    <FormControlLabel control={<Checkbox size="small" checked={!!form.ocultarEmail} onChange={setChk("ocultarEmail")} />} label="Ocultar email" />
                  )}
                </Grid>

                {!ehComum && (
                  <>
                    <Grid item xs={12}>
                      <Divider textAlign="left">
                        <Typography variant="caption">Faixa autorizada pela Vigilância Sanitária</Typography>
                      </Divider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Órgão autorizador" placeholder="VISA-BH"
                        fullWidth size="small"
                        helperText="Complete a cidade/órgão: VISA-BH, VISA Sabará/MG..."
                        value={form.orgaoAutorizacao ?? ""}
                        onChange={(e) => setForm((f) => ({ ...f, orgaoAutorizacao: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField label="Autorização nº (sai no rodapé)" placeholder="31.005552762026-05"
                        fullWidth size="small"
                        helperText={`Rodapé: Aut. ${(form.orgaoAutorizacao || "").trim() || "VISA"} ${form.numeroAutorizacao || ""}`.trim()}
                        value={form.numeroAutorizacao || ""}
                        onChange={(e) => setForm((f) => ({ ...f, numeroAutorizacao: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField label="Numeração inicial" placeholder="2606.1-31.0076916" fullWidth size="small"
                        value={form.numeroInicialTexto || ""}
                        onChange={(e) => setForm((f) => ({ ...f, numeroInicialTexto: aplicarMascara(e.target.value) }))} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField label="Numeração final" placeholder="2606.1-31.0077015" fullWidth size="small"
                        value={form.numeroFinalTexto || ""}
                        onChange={(e) => setForm((f) => ({ ...f, numeroFinalTexto: aplicarMascara(e.target.value) }))} />
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="body2" color={faixa?.erro ? "error" : "textSecondary"}>
                        {faixa?.erro ? "—" : faixa ? `${faixa.quantidade} folha(s)` : "—"}
                      </Typography>
                    </Grid>
                    {faixa?.erro && (
                      <Grid item xs={12}><Alert severity="error">{faixa.erro}</Alert></Grid>
                    )}
                    {conflitos.length > 0 && (
                      <Grid item xs={12}>
                        <Alert severity="error">
                          <b>Numeração já utilizada.</b> Esta faixa se sobrepõe a:{" "}
                          {conflitos.map((c) => (
                            <span key={c.id}>
                              {c.numero} ({c.numeroInicialTexto} a {c.numeroFinalTexto}
                              {c.emitenteNome ? ` — ${c.emitenteNome}` : ""}){" "}
                            </span>
                          ))}
                          . A numeração do SNCR é única; corrija a faixa da autorização.
                        </Alert>
                      </Grid>
                    )}
                    {conflitos.length === 0 && rascunhosSobrepostos.length > 0 && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          Existe(m) rascunho(s) com faixa sobreposta:{" "}
                          {rascunhosSobrepostos.map((c) => c.numero).join(", ")}. Se forem
                          obsoletos, apague-os para evitar numeração duplicada.
                        </Alert>
                      </Grid>
                    )}
                    {faixa?.cruzaVirada && (
                      <Grid item xs={12}>
                        <Alert severity="warning">
                          Esta faixa cruza a virada do último bloco. O sistema vai numerar somando 1 a cada
                          folha <b>Confira na autorização</b> antes
                          de imprimir o talão inteiro.
                        </Alert>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Accordion disableGutters elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="caption">Ajuste fino da posição do número (x: {numeroPos.x} · y: {numeroPos.y})</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          <Button size="small" onClick={() => adjustNumero("x", -5)}>◀ X-5</Button>
                          <Button size="small" onClick={() => adjustNumero("x", 5)}>X+5 ▶</Button>
                          <Button size="small" onClick={() => adjustNumero("y", 5)}>▲ Y+5</Button>
                          <Button size="small" onClick={() => adjustNumero("y", -5)}>▼ Y-5</Button>
                          <Typography variant="caption" sx={{ alignSelf: "center", ml: 1 }}>
                            Gere, confira e ajuste até o número cair no lugar.
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>

            {/* Coluna direita (preview AO VIVO): mostra a página 1 conforme edita,
                sem gerar/consumir faixa. Some quando já há PDF final gerado. */}
            {!pdfUrl && previewUrl && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">
                  Prévia ao vivo {previewLoading ? "— atualizando…" : "— não consome numeração"}
                </Typography>
                <img
                  src={previewUrl}
                  alt="prévia do receituário"
                  style={{ width: "100%", border: "1px solid #ddd", borderRadius: 4, opacity: previewLoading ? 0.6 : 1, transition: "opacity .2s" }}
                />
              </Grid>
            )}

            {/* Coluna direita: pré-visualização do gerado */}
            {pdfUrl && (
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="textSecondary">
                  Pré-visualização {ultimoNumero ? `— último nº: ${ultimoNumero}` : ""}
                </Typography>
                <iframe title="preview" src={pdfUrl} style={{ width: "100%", height: 460, border: "1px solid #ddd", borderRadius: 4 }} />
                <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                  <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>Baixar</Button>
                  <Button size="small" variant={form.arteAprovada ? "text" : "outlined"}
                    startIcon={<PrintIcon />} onClick={handlePrint}>
                    Imprimir
                  </Button>
                  {form.arteAprovada ? (
                    <Chip size="small" color="success" icon={<CheckCircleIcon />}
                      label={`Arte aprovada${form.arteAprovadaPor?.name ? ` por ${form.arteAprovadaPor.name}` : ""}`} />
                  ) : (
                    <Typography variant="caption" color="warning.main">
                      Arte ainda não aprovada pelo cliente
                    </Typography>
                  )}
                </div>
                {!form.arteEnviadaEm && (form.ticketId || initialTicketId) && (
                  <Alert
                    severity="info"
                    sx={{ mt: 1 }}
                    action={
                      <Button size="small" disabled={enviandoProva} onClick={handleEnviarProva}
                        startIcon={enviandoProva ? <CircularProgress size={14} /> : <SendIcon />}>
                        Enviar prova
                      </Button>
                    }
                  >
                    O cliente recebe uma <b>imagem de prova</b> com marca d'água — nunca o PDF.
                  </Alert>
                )}
                {!form.arteEnviadaEm && !(form.ticketId || initialTicketId) && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Envie a prova ao cliente pelo chat (ícone de balão na lista) antes de registrar a aprovação.
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
        )}
        {ehComum && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Receituário <b>simples, não controlado</b> — sem faixa SNCR e sem autorização da
            Vigilância Sanitária (não é documento do SNCR). Use para receituário comum
            com a logo e os dados do emitente.
          </Alert>
        )}
        {!ehComum && form.modo === "recriado" && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Formulário recriado conforme o modelo oficial da ANVISA, em <b>fundo branco</b> —
            a cor vem do papel. Os dados do emitente e o rodapé da gráfica ocupam o lugar dos
            textos de instrução do modelo. Campos de paciente, comprador e prescrição saem em branco.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button onClick={handleSaveDraft} disabled={loading}>Salvar rascunho</Button>
        <Button variant="contained" onClick={handleGenerate} disabled={generating || !podeGerar}
          startIcon={generating ? <CircularProgress size={16} /> : <AutorenewIcon />}>
          {pdfUrl
            ? "Regerar"
            : `Gerar${ehComum
                ? ` ${Math.max(1, parseInt(form.quantidade, 10) || 1)} folha(s)`
                : faixa && !faixa.erro ? ` ${faixa.quantidade} folha(s)` : ""}`}
        </Button>
      </DialogActions>

      {/* Confirmação da aprovação da arte pelo cliente, antes de produzir. */}
      <Dialog open={aprovacaoOpen} onClose={() => setAprovacaoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar aprovação da arte</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            O talão é documento controlado. Confirme que <b>o cliente aprovou esta arte</b> antes
            de imprimir — regerar depois invalida a aprovação e a numeração já terá saído.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Receituário <b>{form.numero}</b> · {form.emitenteNome || "sem prescritor"}
            {faixa && !faixa.erro && <> · {faixa.quantidade} folha(s)</>}
            <br />
            Faixa: <code>{form.numeroInicialTexto} a {form.numeroFinalTexto}</code>
          </Typography>
          <TextField
            label="Observação (opcional)" placeholder="Ex.: aprovado por WhatsApp em 09/07"
            fullWidth size="small" multiline rows={2}
            value={aprovacaoObs} onChange={(e) => setAprovacaoObs(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAprovacaoOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="success" disabled={aprovando}
            onClick={handleConfirmarAprovacao}
            startIcon={aprovando ? <CircularProgress size={16} /> : <CheckCircleIcon />}>
            Cliente aprovou — imprimir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Autorização com várias receitas: escolher quais viram receituário. */}
      {importPendente && (
        <Dialog open onClose={() => setImportPendente(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Esta autorização traz {importPendente.dados.receitas.length} receitas</DialogTitle>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 2 }}>
              A VISA liberou mais de um modelo na mesma autorização. Marque <b>quais o
              cliente pediu</b>: cada um vira um receituário separado, com sua própria
              prova, aprovação e impressão.
            </Alert>
            {importPendente.dados.receitas.map((r, i) => {
              const m = Array.isArray(models) ? models.find((x) => x.key === r.modelo) : null;
              const sw = PAPEL_SWATCH[m?.papel] || PAPEL_SWATCH.branco;
              const qtd = calcularFaixa(r.numeroInicialTexto, r.numeroFinalTexto);
              return (
                <FormControlLabel
                  key={`${r.modelo}-${i}`}
                  sx={{ display: "flex", alignItems: "flex-start", mb: 1 }}
                  control={
                    <Checkbox
                      checked={importPendente.marcadas[i]}
                      onChange={(e) => setImportPendente((p) => ({
                        ...p,
                        marcadas: p.marcadas.map((v, j) => (j === i ? e.target.checked : v)),
                      }))}
                    />
                  }
                  label={
                    <span>
                      <b>{m?.label || r.modelo}</b>{" "}
                      <Chip size="small" label={`papel ${sw.nome.toLowerCase()}`}
                        style={{ backgroundColor: sw.bg, border: "1px solid #bbb", height: 20 }} />
                      <br />
                      <Typography variant="caption" component="span">
                        <code>{r.numeroInicialTexto} a {r.numeroFinalTexto}</code>
                        {qtd && !qtd.erro ? ` — ${qtd.quantidade} folha(s)` : ""}
                      </Typography>
                    </span>
                  }
                />
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImportPendente(null)}>Cancelar</Button>
            <Button variant="contained" disabled={criandoReceitas}
              onClick={handleConfirmarReceitas}
              startIcon={criandoReceitas ? <CircularProgress size={16} /> : <CheckCircleIcon />}>
              Usar selecionadas
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Cada modelo imprime em papel de outra cor: a pausa dá tempo de trocar. */}
      <Dialog open={papelConfirmOpen} onClose={() => setPapelConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confira o papel na impressora</DialogTitle>
        <DialogContent dividers>
          {(() => {
            const sw = PAPEL_SWATCH[selectedModel?.papel] || PAPEL_SWATCH.branco;
            return (
              <div style={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <b>{selectedModel?.label || form.modelo}</b> imprime em papel:
                </Typography>
                <div style={{
                  background: sw.bg, border: "1px solid #999", borderRadius: 6,
                  padding: "14px 0", fontWeight: 700, letterSpacing: 2, marginBottom: 12,
                }}>
                  {sw.nome}
                </div>
                <Typography variant="caption" color="textSecondary">
                  Coloque o papel certo na impressora e confirme. Se for imprimir outros
                  modelos, imprima um por vez, trocando o papel entre eles.
                </Typography>
              </div>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPapelConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<PrintIcon />}
            onClick={() => { setPapelConfirmOpen(false); imprimir(); }}>
            Papel pronto — imprimir
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ReceituarioDialog;
